/**
 * editTurnPricing.ts — WEB-1847: какая цена у хода ИИ-чата и как её подписать.
 *
 * ЗАЧЕМ ЗЕРКАЛО ПРАВИЛА НА КЛИЕНТЕ, А НЕ ЗАПРОС К СЕРВЕРУ. Решением владельца
 * 01.09 у хода две модели цены: мелкий шаг — фиксированная ставка (18
 * кредитов тогда, 25 после решения владельца 13.09 — WEB-1847), крупный —
 * списание ПО ФАКТУ. Само число сюда не вписано: оно приходит каталогом цен
 * от сервера (см. абзац «ЧИСЛА ЗДЕСЬ НЕ ЖИВУТ» ниже), поэтому подъём ставки
 * этот файл не трогает. Клиент обязан видеть цену ДО отправки
 * (сценарий приёмки №3), а модель зависит от того, что он прямо сейчас
 * набрал в поле и приложил ли файл. Отдельная дверь оценки означала бы запрос
 * к серверу на каждую букву задания — либо подпись, отстающую от набранного
 * текста, то есть врущую. Правило же короткое и целиком складывается из того,
 * что у клиента и так на экране, поэтому оно зеркалится сюда.
 *
 * ЧТОБЫ ЗЕРКАЛО НЕ РАЗЪЕХАЛОСЬ, на бэкенде стоит сторож паритета
 * `edit-turn-pricing-mirror.contract.spec.ts`: он читает ЭТОТ файл и сверяет
 * список глаголов и порог с серверными (detect-intermediate-stop.util.ts,
 * edit-turn-classification.util.ts). Разъедутся — прогон краснеет.
 *
 * ЧИСЛА ЗДЕСЬ НЕ ЖИВУТ. И фикс-цена, и оценка сверху приходят каталогом цен
 * от сервера (billingSlice.fetchCostCatalogThunk). Вписанное руками число
 * молча разошлось бы со списанием — класс дефекта, ради которого числа
 * вообще едут каталогом (WEB-830, WEB-1401).
 *
 * ОКОНЧАТЕЛЬНОЕ СЛОВО — ЗА СЕРВЕРОМ. Модель цены выбирает сервер в момент
 * приёма хода и называет её в ответе 202 (`pricingModel`), фактическую сумму
 * отдаёт дверь итога хода. Эта утилита нужна только подписи ДО отправки.
 */

/** Ключ фиксированной цены хода в плоском каталоге цен (операция чата). */
export const EDIT_TURN_FIXED_COST_KEY = 'editor_turn_sonnet';

/** Ключ оценки сверху крупного хода БЕЗ приложенного образца. */
export const EDIT_TURN_METERED_ESTIMATE_KEY = 'edit_turn_metered_estimate';

/** Ключ оценки сверху крупного хода С приложенным образцом. */
export const EDIT_TURN_METERED_ESTIMATE_WITH_REFERENCE_KEY =
  'edit_turn_metered_estimate_with_reference';

/**
 * Сколько отдельных действий в задании делают ход крупным. Зеркало
 * EDIT_TURN_MULTI_ACTION_THRESHOLD с бэкенда.
 */
export const EDIT_TURN_MULTI_ACTION_THRESHOLD = 2;

/**
 * Глаголы-приказы, по которым считается число заказанных действий. Зеркало
 * REQUESTED_ACTION_VERBS с бэкенда (detect-intermediate-stop.util.ts, WEB-1843).
 * Список закрытый намеренно: «умное» разбиение по запятым принимало бы
 * уточнения за отдельные действия.
 */
export const EDIT_TURN_ACTION_VERBS: readonly string[] = [
  // Русские глаголы — это слова, которые набирает САМ человек, а не
  // надписи кабинета: их состав дословно сверяется с сервером сторожем
  // паритета, переводить их нельзя. Порядок и состав не менять.
  'поменяй', 'поменяйте', 'измени', 'измените', 'добавь', 'добавьте', // i18n-allow: зеркало серверного списка глаголов
  'поставь', 'поставьте', 'убери', 'уберите', 'удали', 'удалите', // i18n-allow: зеркало серверного списка глаголов
  'сделай', 'сделайте', 'замени', 'замените', 'создай', 'создайте', // i18n-allow: зеркало серверного списка глаголов
  'напиши', 'напишите', 'покрась', 'обнови', 'обновите', 'увеличь', // i18n-allow: зеркало серверного списка глаголов
  'уменьши', 'перенеси', 'перемести', 'сгенерируй', 'вставь', 'выровняй', // i18n-allow: зеркало серверного списка глаголов
  'переделай', 'собери', 'настрой', 'примени', 'задай', 'оформи', // i18n-allow: зеркало серверного списка глаголов
  'дополни', 'переведи', // i18n-allow: зеркало серверного списка глаголов
  'add', 'change', 'put', 'remove', 'delete', 'make',
  'replace', 'create', 'write', 'update', 'generate', 'set',
  'build', 'move', 'increase', 'decrease', 'apply', 'rename',
];

/** Класс буквы — тот же, что у серверного счётчика (границы слова в юникоде). */
const LETTER = '[\\p{L}\\p{N}_]';

const ACTION_RE = new RegExp(
  `(?<!${LETTER})(${EDIT_TURN_ACTION_VERBS.join('|')})(?!${LETTER})`,
  'giu',
);

/**
 * WEB-2374 п.8 — текст в кавычках это надпись, которую человек просит
 * поставить, а не второй приказ: «замени надпись на «Сделай заказ»» — одно
 * действие. Дословное зеркало QUOTED_TEXT_RE_SOURCE с сервера
 * (backend/src/ai-funnels/detect-intermediate-stop.util.ts); совпадение строка
 * в строку проверяет сторож паритета WEB-1847.
 */
export const QUOTED_TEXT_RE = /«[^»]*»|“[^”]*”|"[^"]*"|‘[^’]*’|'[^'\n]*'/gu;

/** Убрать из задания содержимое кавычек вместе с самими кавычками. */
export function stripQuotedTextClient(instruction: string): string {
  return instruction.replace(QUOTED_TEXT_RE, ' ');
}

/** Сколько отдельных действий названо в задании. Зеркало countRequestedActions. */
export function countRequestedActionsClient(instruction: string): number {
  if (!instruction) return 0;
  // Флаг g хранит lastIndex — считаем через match, а не exec.
  return (stripQuotedTextClient(instruction).match(ACTION_RE) ?? []).length;
}

/**
 * WEB-2374 п.1 — ХОД-ПРОДОЛЖЕНИЕ ПО ОБРАЗЦУ. Ход вроде «продолжай» / «ну?» /
 * ответ на вопрос о размере сюжета делает ту же тяжёлую работу, что ход с
 * приложенным файлом, и сервер берёт за него КРУПНУЮ модель цены (решение
 * владельца 2369-Р2 «Оплата по факту», серверная функция `isReferenceFollowUp`
 * в backend/src/ai-funnels/services/edit-thread-prior-turns.util.ts). Признак
 * виден кабинету и ДО отправки: он считается по последнему ходу из уже
 * загруженного списка ходов — там едут поля `referenceAttached` и
 * `referenceStoryPending`.
 *
 * ЧЕГО ЗЕРКАЛО НЕ ПОВТОРЯЕТ (честно). У сервера есть запасной путь для ходов,
 * записанных ДО миграции 1900000000223: там колонки нет (null) и признак
 * «спросил размер сюжета» достаётся поиском текста промпта в пересказе. Сюда
 * этот путь не переносится — промпт в кабинете не живёт. Для таких
 * исторических ходов подпись покажет цену мелкого шага, а сервер возьмёт
 * крупную; окончательное слово всё равно за сервером (он называет модель в
 * ответе 202), и случай сужается до нитей, начатых до миграции.
 */
export interface EditTurnPriorTurnFlags {
  turnNumber: number;
  referenceAttached?: boolean | null;
  referenceStoryPending?: boolean | null;
}

/**
 * ОБЩИЙ НАБОР ОБРАЗЦОВ ОБОИХ КОНЦОВ (правило WEB-1844). По нему судится и
 * кабинет (frontend/src/utils/__tests__/editTurnPricing.test.ts), и сервер
 * (backend/src/ai-funnels/cost/edit-turn-pricing-mirror.contract.spec.ts,
 * который вычитывает эту строку прямо из файла). Строка, а не объект, —
 * чтобы серверный сторож мог разобрать её без сборки фронта.
 */
export const EDIT_TURN_FOLLOW_UP_EXAMPLES_JSON = `[
  {
    "name": "empty thread — nothing to continue",
    "priorTurns": [],
    "expected": false
  },
  {
    "name": "sample attached to the last turn",
    "priorTurns": [{ "turnNumber": 1, "referenceAttached": true }],
    "expected": true
  },
  {
    "name": "last turn asked about story size",
    "priorTurns": [{ "turnNumber": 4, "referenceStoryPending": true }],
    "expected": true
  },
  {
    "name": "sample two turns back — depth is one turn",
    "priorTurns": [
      { "turnNumber": 1, "referenceAttached": true },
      { "turnNumber": 2, "referenceAttached": false }
    ],
    "expected": false
  },
  {
    "name": "list order does not matter — highest turn number wins",
    "priorTurns": [
      { "turnNumber": 5, "referenceAttached": true },
      { "turnNumber": 2, "referenceAttached": false }
    ],
    "expected": true
  },
  {
    "name": "columns empty (turn predates the migration) — flag stays down",
    "priorTurns": [
      { "turnNumber": 3, "referenceAttached": null, "referenceStoryPending": null }
    ],
    "expected": false
  },
  {
    "name": "ordinary turn without a sample",
    "priorTurns": [
      { "turnNumber": 7, "referenceAttached": false, "referenceStoryPending": false }
    ],
    "expected": false
  }
]`;

/**
 * WEB-2401 — образец хода из проекта: своя сущность (квиз, пейволл, воронка)
 * или каркас по ссылке. Зеркало серверного `EditTurnReferenceRef`.
 */
export interface EditTurnReferenceClient {
  kind: 'quiz' | 'paywall' | 'funnel' | 'blueprint';
  id: string;
}

/**
 * WEB-2401 — «к ходу приложен образец». Зеркало серверной
 * `editTurnHasReference` (backend/src/ai-funnels/services/edit-turn-reference.ts):
 * файл или образец из проекта любого вида. Результат идёт в `hasAttachments`
 * правила цены — ход с образцом из проекта «по факту», как ход с файлом.
 */
export function editTurnHasReferenceClient({
  attachments = 0,
  reference = null,
}: {
  attachments?: number;
  reference?: EditTurnReferenceClient | null;
}): boolean {
  return attachments > 0 || !!reference;
}

/**
 * WEB-2401 — общий набор образцов признака «образец есть». Лежит строкой JSON
 * по той же причине, что набор продолжения выше: сервер читает его разбором
 * текста (сторож паритета edit-turn-pricing-mirror.contract.spec.ts) и судит
 * по нему `editTurnHasReference`, кабинет — `editTurnHasReferenceClient`.
 */
export const EDIT_TURN_HAS_REFERENCE_EXAMPLES_JSON = `[
  { "name": "nothing attached", "attachments": 0, "reference": null, "expected": false },
  { "name": "one file", "attachments": 1, "reference": null, "expected": true },
  { "name": "own quiz", "attachments": 0, "reference": { "kind": "quiz", "id": "q-1" }, "expected": true },
  { "name": "own paywall", "attachments": 0, "reference": { "kind": "paywall", "id": "p-1" }, "expected": true },
  { "name": "own funnel (parent)", "attachments": 0, "reference": { "kind": "funnel", "id": "f-1" }, "expected": true },
  { "name": "blueprint from a link", "attachments": 0, "reference": { "kind": "blueprint", "id": "b-1" }, "expected": true },
  { "name": "file and own funnel", "attachments": 2, "reference": { "kind": "funnel", "id": "f-1" }, "expected": true }
]`;

/**
 * Продолжает ли следующий ход работу по образцу. Зеркало серверной
 * `isReferenceFollowUp`: смотрим ровно на ОДИН ход назад (иначе нить после
 * первого же образца навсегда осталась бы «референсной»).
 */
export function isReferenceFollowUpClient(
  priorTurns: readonly EditTurnPriorTurnFlags[] | null | undefined,
): boolean {
  const last = [...(priorTurns ?? [])].sort(
    (a, b) => b.turnNumber - a.turnNumber,
  )[0];
  if (!last) return false;
  return last.referenceStoryPending === true || last.referenceAttached === true;
}

export type EditTurnPricingModel = 'fixed' | 'metered';

export interface EditTurnPricingInput {
  instruction: string;
  hasAttachments: boolean;
  /** WEB-2374 п.1: этот ход продолжает работу по образцу (см. выше). */
  referenceFollowUp?: boolean;
}

/**
 * Мелкий шаг или крупный. Чистая функция от того, что клиент видит на экране:
 * приложен ли образец и что написано в поле. Одно и то же на входе всегда даёт
 * одно и то же на выходе (требование предсказуемости, сценарий приёмки №4).
 */
export function classifyEditTurnPricingClient({
  instruction,
  hasAttachments,
  referenceFollowUp = false,
}: EditTurnPricingInput): EditTurnPricingModel {
  if (hasAttachments) return 'metered';
  // WEB-2374 п.1 — тот же порядок проверок, что на сервере.
  if (referenceFollowUp) return 'metered';
  return countRequestedActionsClient(instruction ?? '') >=
    EDIT_TURN_MULTI_ACTION_THRESHOLD
    ? 'metered'
    : 'fixed';
}

function readPositiveNumber(
  catalog: Record<string, number> | null | undefined,
  key: string,
): number | null {
  const value = catalog?.[key];
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return value;
}

/**
 * Цена, которую подписываем у поля ввода: у мелкого шага — его фиксированная
 * цена, у крупного — ОЦЕНКА СВЕРХУ («до ~N кредитов, спишется по факту»).
 * null — каталог не загружен или сервер старый: подпись тогда молчит про
 * число, врать нельзя (деньги).
 */
export function readEditTurnPriceCredits(
  catalog: Record<string, number> | null | undefined,
  {
    pricingModel,
    hasAttachments,
    referenceFollowUp = false,
  }: {
    pricingModel: EditTurnPricingModel;
    hasAttachments: boolean;
    referenceFollowUp?: boolean;
  },
): number | null {
  if (pricingModel === 'fixed') {
    return readPositiveNumber(catalog, EDIT_TURN_FIXED_COST_KEY);
  }
  // WEB-2374 п.1: у продолжения по образцу работа та же, что у хода с
  // приложенным файлом, — и оценка сверху берётся по тому же ключу.
  return readPositiveNumber(
    catalog,
    hasAttachments || referenceFollowUp
      ? EDIT_TURN_METERED_ESTIMATE_WITH_REFERENCE_KEY
      : EDIT_TURN_METERED_ESTIMATE_KEY,
  );
}
